import _ from 'lodash'

import { mapActions, mapGetters, mapState } from 'vuex'

import scrollHelper from './scroll-helper'
import useCheckHelpers from '@app/application/communication/sections/communication/composables/use-check-helpers'
import { isVisible } from '@app/utils/domHelpers'

export default {
  mixins: [scrollHelper],

  data() {
    return {
      // Storing this as local state, because only this component needs to know about it
      showingCompletedTodosAndCheckLists: false,
      anchorElementScrolled: false,
    }
  },

  created() {
    this.fetchCommunication()
    this.fetchMentionUsernames()
    // Event handlers
    window.eventHub.$on('refreshCommunicationData', this.fetchCommunication)
    window.eventHub.$on('resetCommunicationData', this.resetCommunicationData)
  },

  beforeUnmount() {
    window.eventHub.$off('refreshCommunicationData', this.fetchCommunication)
    window.eventHub.$off('resetCommunicationData', this.resetCommunicationData)
  },

  methods: {
    ...mapActions([
      'fetchCommunication',
      'fetchMentionUsernames',
      'resetCommunicationData',
    ]),

    toggleCompletedTodosAndCheckLists() {
      this.showingCompletedTodosAndCheckLists =
        !this.showingCompletedTodosAndCheckLists
    },

    expandHiddenItems() {
      const id = this.elementAnchorLink().split('-')[1]

      return Promise.resolve().then(() => {
        const anchorElement = this.$el.querySelector(this.elementAnchorLink())
        // Return if element already exists among the pending items
        if (isVisible(anchorElement)) {
          return
        }

        // Toggle completed todos section if anchored element lives there
        else if (this.sortedCompletedTodos.find((c) => c.id === id)) {
          this.showingCompletedTodosAndCheckLists = true
        }

        // Toggle completed checks in checklists section if anchored element lives there
        // This expands multi level completed checks(partially and fully complete checklists) as required
        else if (this.checkIds.find((c) => c === id)) {
          _.forEach(this.$refs.checkList, (checkList) => {
            if (checkList.completedCheckIds.find((c) => c === id)) {
              checkList.toggleCompletedChecks()
            }
          })
        }
      })
    },
  },

  computed: {
    ...mapGetters([
      'fetchingCommunicationsInProgress',
      'notes',
      'todos',
      'checkListChecks',
    ]),

    ...mapState({
      checkLists: (state) => state.checks.checkLists,
      contextType: (state) => state.contextType,
      contextId: (state) => state.contextId,
      ledgerId: (state) => state.ledgerId,
      storeLedgerId: (state) => state.ledgerId,
      ledgerPeriodName: (state) => state.ledgerPeriodName,
    }),

    showNotesEmptyState() {
      return this.sortedNotes.length === 0
    },

    showTodosEmptyState() {
      let count =
        this.sortedPendingTodos.length + this.sortedPendingCheckLists.length
      if (this.showingCompletedTodosAndCheckLists) {
        count =
          count +
          this.sortedCompletedTodos.length +
          this.sortedCompletedCheckLists.length
      }
      return count === 0
    },

    completedCount() {
      return (
        this.sortedCompletedTodos.length + this.sortedCompletedCheckLists.length
      )
    },

    sortedNotes() {
      return _.orderBy(
        this.notes,
        ['sticky', 'lastCommentedAt'],
        ['desc', 'desc']
      )
    },

    sortedTodos() {
      return _.orderBy(
        this.todos,
        ['sticky', 'lastCommentedAt'],
        ['desc', 'desc']
      )
    },

    sortedCompletedTodos() {
      return this.sortedTodos.filter((todo) => todo.done)
    },

    sortedPendingTodos() {
      return this.sortedTodos.filter((todo) => !todo.done)
    },

    sortedCheckLists() {
      return _.orderBy(
        this.checkLists[this.storeLedgerId || this.ledgerId],
        ['text'],
        ['asc']
      )
    },

    sortedCompletedCheckLists() {
      const { checkCheckedByAllParticipants } = useCheckHelpers()

      return this.sortedCheckLists.filter((checkList) =>
        this.checkListChecks(
          this.storeLedgerId || this.ledgerId,
          checkList.id
        ).every((check) => {
          return checkCheckedByAllParticipants(check)
        })
      )
    },

    sortedPendingCheckLists() {
      return _.without(this.sortedCheckLists, ...this.sortedCompletedCheckLists)
    },

    checkIds() {
      return _.flatMap(this.$refs.checkList, (checkList) => checkList.checkIds)
    },
  },
}
